import {
  type CreateSupplierRequest,
  CurrencyOptionListSchema,
  type DocumentDownloadResponse,
  DocumentDownloadResponseSchema,
  type SupplierBulkRecategorizeRequest,
  type SupplierDetail,
  SupplierDetailSchema,
  type SupplierDocumentKind,
  SupplierDocumentUploadInitResponseSchema,
  type SupplierListQuery,
  type SupplierListResponse,
  SupplierListResponseSchema,
  type UpdateSupplierRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Supplier master API. Every response is validated with the shared Zod schema
 * so a server change can't sneak past the UI types.
 */

export const listSuppliers = async (query: SupplierListQuery): Promise<SupplierListResponse> => {
  const { data } = await apiClient.get<unknown>('/suppliers', { params: query });
  return SupplierListResponseSchema.parse(data);
};

export const getSupplier = async (id: string): Promise<SupplierDetail> => {
  const { data } = await apiClient.get<unknown>(`/suppliers/${id}`);
  return SupplierDetailSchema.parse(data);
};

export const createSupplier = async (payload: CreateSupplierRequest): Promise<SupplierDetail> => {
  const { data } = await apiClient.post<unknown>('/suppliers', payload);
  return SupplierDetailSchema.parse(data);
};

export const updateSupplier = async (
  id: string,
  payload: UpdateSupplierRequest,
): Promise<SupplierDetail> => {
  const { data } = await apiClient.patch<unknown>(`/suppliers/${id}`, payload);
  return SupplierDetailSchema.parse(data);
};

export const deleteSupplier = async (id: string): Promise<void> => {
  await apiClient.delete(`/suppliers/${id}`);
};

export const bulkDeleteSuppliers = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/suppliers/bulk-delete', { ids });
  return data;
};

export const bulkSetSuppliersStatus = async (
  ids: string[],
  isActive: boolean,
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/suppliers/bulk-set-status', { ids, isActive });
  return data;
};

export const bulkRecategorizeSuppliers = async (
  payload: SupplierBulkRecategorizeRequest,
): Promise<{ updated: number }> => {
  const { data } = await apiClient.post<{ updated: number }>(
    '/suppliers/bulk-recategorize',
    payload,
  );
  return data;
};

// ----- Proof documents (GST certificate, PAN card, bank proof) -----

/**
 * Upload one proof document: the server issues a presigned PUT, the file goes
 * straight to S3 (plain `fetch` - a presigned URL carries its own auth and
 * rejects ours), and the returned key is saved with the supplier form.
 */
export const uploadSupplierDocument = async (
  kind: SupplierDocumentKind,
  file: File,
): Promise<string> => {
  const { data } = await apiClient.post<unknown>('/suppliers/documents/upload-init', {
    kind,
    filename: file.name,
    mimeType: file.type,
    sizeBytes: file.size,
  });
  const init = SupplierDocumentUploadInitResponseSchema.parse(data);
  const res = await fetch(init.presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!res.ok) throw new Error(`Upload failed (${res.status}). Try again.`);
  return init.s3Key;
};

export const downloadSupplierDocument = async (
  id: string,
  kind: SupplierDocumentKind,
): Promise<DocumentDownloadResponse> => {
  const { data } = await apiClient.get<unknown>(`/suppliers/${id}/documents/${kind}/download`);
  return DocumentDownloadResponseSchema.parse(data);
};

// ----- Currency lookup -----

export const listCurrencyOptions = async () => {
  const { data } = await apiClient.get<unknown>('/currencies/options');
  return CurrencyOptionListSchema.parse(data);
};
